import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { Layout, Image, Menu, Space, Drawer } from "antd";
import { MenuUnfoldOutlined, MenuFoldOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BrowserView, MobileView } from "react-device-detect";
import classNames from "classnames";

import { getDataLocalStorage } from "../../utils/helpers/localStorageHelper";
import ThemeContext from "../../utils/context/Theme/ThemeContext";
import { stdSideDashItems, teacSideDashItems } from "./SideDashItems";
//images
import lightLogoImg from "../../assets/images/lightLogo.png";
import lightLogoIco from "../../assets/images/lightLogoIco.png";
import darkLogoImg from "../../assets/images/logo.png";
import darkLogoIco from "../../assets/images/darkLogoIco.png";

import "./DashboardSideBar.css";

const DashboardSideBar = ({ collapsed, setCollapsed }) => {
  const { Sider } = Layout;

  const { t, i18n } = useTranslation("sideBar");
  const [visible, setVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState("1");
  const { themeMode } = useContext(ThemeContext);

  const location = useLocation();

  const role = getDataLocalStorage("role");

  const items = role === "instructor" ? teacSideDashItems : stdSideDashItems;

  const transLabel =
    role === "instructor" ? "teacSideBarItems" : "stdSideBarItems";

  const showDrawer = useCallback(() => setVisible(true), []);
  const onClose = useCallback(() => setVisible(false), []);

  const toggle = useCallback(() => {
    setCollapsed(() => !collapsed);
  }, [setCollapsed, collapsed]);

  // // Update selectedKey based on current location
  // const handleMenuClick = useCallback(({ key }) => {
  //   setSelectedKey(key);
  // }, []);

  useEffect(() => {
    const currentPath = location.pathname;
 
    const findItem = (items) => {
      for (const item of items) {
        if (item.link?.split("#")[0] === currentPath) {
          return item;
        }
        if (item.children) {
          const child = item.children.find(
            (c) => c.link.split("#")[0] === currentPath,
          );
          if (child) return item;
        }
      }
      return null;
    };

    const currentItem = findItem(items);
    if (currentItem) {
      setSelectedKey(currentItem.key);
    }
  }, [location.pathname, items]);

  const menuItems = useMemo(() => {
    return items.map((item) => {
      if (item.children) {
        return {
          key: item.key,
          icon: (
            <Image
              preview={false}
              src={themeMode === "dark" ? item.darkIcon : item.lightIcon}
              alt="icon"
              width={27}
              height={27}
              className="side-ico"
            />
          ),
          label: (
            <div className="ms-10">
              {t(`${transLabel}.item${item.key}.label`)}
            </div>
          ),
          children: item.children.map((child, inx) => ({
            key: child.key,
            label: (
              <Link to={child.link} className="ms-10">
                {t(`${transLabel}.item${item.key}.children.test${inx + 1}`, {
                  returnObjects: true,
                })}
              </Link>
            ),
          })),
        };
      }

      return {
        key: item.key,
        icon: item.ico ? (
          item.ico
        ) : (
          <Image
            preview={false}
            src={themeMode === "dark" ? item.darkIcon : item.lightIcon}
            alt="icon"
            width={27}
            height={27}
            className="side-ico"
          />
        ),
        label: (
          <Link to={item.link} className="ms-10">
            {t(`${transLabel}.item${item.key}`)}
          </Link>
        ),
      };
    });
  }, [themeMode, t, items, transLabel]);

  return (
    <>
      <BrowserView>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme={themeMode}
          style={{ transition: "none" }}
          className={
            collapsed ? "pt-30 collapsed-sider" : "pt-30 expanded-sider"
          }
        >
          <Link to="/" className="mt-30 logoPreview">
            {collapsed ? (
              <Image
                src={themeMode === "dark" ? lightLogoIco : darkLogoIco}
                width={60}
                preview={false}
              />
            ) : (
              <Image
                src={themeMode === "dark" ? lightLogoImg : darkLogoImg}
                className="logo-image"
                preview={false}
                title="logo-image"
              />
            )}
          </Link>
          {React.createElement(
            collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
            {
              className: "trigger text-wh[ite",
              onClick: toggle,
            },
          )}
          <Menu
            mode="inline"
            theme={themeMode}
            items={menuItems}
            selectedKeys={[selectedKey]}
            className="border-0"
           // onClick={handleMenuClick}
          />
        </Sider>
      </BrowserView>

      <MobileView
        className={classNames(
          "mobile-dash-sider mt-5",
          themeMode === "dark" && "mob-sider-dark",
        )}
      >
        <Space size={24}>
          <MenuFoldOutlined onClick={showDrawer} />
          <Link to="/" className="logoPreview">
            {collapsed ? (
              themeMode === "dark" ? (
                <Image src={lightLogoIco} width={60} preview={false} />
              ) : (
                <Image src={darkLogoIco} width={60} preview={false} />
              )
            ) : themeMode === "light" ? (
              <Image
                src={darkLogoImg}
                className="logo-image"
                preview={false}
                title="logo-image"
              />
            ) : (
              <Image
                src={lightLogoImg}
                className="logo-image"
                preview={false}
                title="logo-image"
              />
            )}
          </Link>
        </Space>
        <Drawer
          title={t("homeList", { ns: "placeholders" })}
          placement={i18n.dir() === "rtl" ? "right" : "left"}
          width={300}
          onClose={onClose}
          open={visible}
          className={classNames(
            "dash-sider",
            themeMode === "dark" && "dash-sider-dark",
          )}
        >
          <Menu
            mode="inline"
            theme={themeMode}
            items={menuItems}
            selectedKeys={[selectedKey]}
            className="border-0"
            //onClick={handleMenuClick}
          />
        </Drawer>
      </MobileView>
    </>
  );
};

export default DashboardSideBar;
